/**
 * Initiative charts component
 * @module components/initiatives/initiatives-charts
 */

import { ParticipationChart } from './participation-chart';
import { BudgetUsageChart } from './budget-usage-chart';
import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import type { InitiativeStatistics } from '@/types/initiative';

interface InitiativesChartsProps {
  locale: Locale;
  isDark: boolean;
  data: InitiativeStatistics | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Display initiative participation growth and budget usage charts
 */
export function InitiativesCharts({ locale, isDark, data, loading, error, refetch }: InitiativesChartsProps) {

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card p-6 rounded-lg shadow-sm border border-border">
          <LoadingSpinner size="lg" label={t(locale, 'loading')} />
        </div>
        <div className="bg-card p-6 rounded-lg shadow-sm border border-border">
          <LoadingSpinner size="lg" label={t(locale, 'loading')} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        title={t(locale, 'error')}
        message={error}
        onRetry={refetch}
        retryLabel={t(locale, 'retry')}
      />
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
      <ParticipationChart
        growth={data.participation_growth}
        locale={locale}
        isDark={isDark}
      />
      <BudgetUsageChart
        budgetUsage={data.budget_usage}
        locale={locale}
        isDark={isDark}
      />
    </div>
  );
}
